import { itemUrl } from "../lib/itemUrl";
import { useCopy } from "./useCopy";

interface IdChipProps {
  /** Human key, e.g. PATPMS-216. Changes when an item moves; old keys stay aliases. */
  itemKey: string;
  /** Global ID, e.g. GR-00A7K3-F. Never changes. */
  globalId: string;
}

/**
 * Shows `KEY · GLOBAL-ID` (UX-01 4.1). "Copy key" copies the key only; "Copy ID" copies the GR- global ID only.
 * Each accessible name starts with its visible identifier (WCAG 2.5.3). The confirmation shows for 2 s and is
 * announced once through the app's polite live region.
 * GRF-27: "Copy link" beside the chip copies the item page's full address and confirms with a toast.
 */
export function IdChip({ itemKey, globalId }: IdChipProps) {
  const { copy, confirmation } = useCopy(2000);
  const link = useCopy(2000, { toast: true });

  return (
    <span className="gf-idchip-wrap">
      <span className="gf-idchip">
        <button type="button" aria-label={`${itemKey} Copy key`} title="Copy key" onClick={() => void copy(itemKey)}>
          {itemKey}
        </button>
        <span className="sep" aria-hidden="true">
          ·
        </span>
        <button type="button" aria-label={`${globalId} Copy ID`} title="Copy global ID" onClick={() => void copy(globalId)}>
          {globalId}
        </button>
      </span>
      <button
        type="button"
        className="gf-copylink"
        title={`Copy a link to ${itemKey}`}
        onClick={() => void link.copy(itemUrl(itemKey), `Link to ${itemKey} copied`)}
      >
        <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14" focusable="false">
          <path
            d="M6.5 9.5a3 3 0 0 0 4.24 0l2.12-2.12a3 3 0 0 0-4.24-4.24l-.7.7M9.5 6.5a3 3 0 0 0-4.24 0L3.14 8.62a3 3 0 0 0 4.24 4.24l.7-.7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        Copy link
      </button>
      {confirmation && (
        <span className="gf-idchip-status" aria-hidden="true">
          {confirmation}
        </span>
      )}
    </span>
  );
}
